import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import './ViewerPage.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || window.location.origin;

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'turn:46.225.89.120:3478', username: 'sentinel', credential: 'sentinel2024' },
];

export default function ViewerPage({ onBack }) {
  const [cameras, setCameras] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [clips, setClips] = useState([]);
  const [tab, setTab] = useState('live');
  const [socketConnected, setSocketConnected] = useState(false);
  const [activeCamera, setActiveCamera] = useState(null);

  const socketRef = useRef(null);
  const wakeLockRef = useRef(null);

  useEffect(() => {
    async function requestWakeLock() {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          wakeLockRef.current.addEventListener('release', () => {
            if (document.visibilityState === 'visible') requestWakeLock();
          });
        }
      } catch (err) { console.warn('[WakeLock viewer]', err.message); }
    }
    requestWakeLock();
    return () => { if (wakeLockRef.current) wakeLockRef.current.release(); };
  }, []);
  // Key: camId -> { pc: RTCPeerConnection, stream: MediaStream }
  const peersRef = useRef({});
  // Key: camId -> video element
  const videoRefs = useRef({});
  // Key: camId -> stream (for re-render)
  const [streams, setStreams] = useState({});

  const addAlert = useCallback((camId, camName) => {
    const alert = {
      id: Date.now(),
      camId,
      camName,
      time: new Date().toLocaleTimeString('es-ES'),
      date: new Date().toLocaleDateString('es-ES'),
    };
    setAlerts(prev => [alert, ...prev].slice(0, 50));
    if (Notification.permission === 'granted') {
      new Notification(`🚨 Movimiento detectado`, { body: `Cámara: ${camName}` });
    }
  }, []);

  const attachStream = useCallback((camId, stream) => {
    setStreams(prev => ({ ...prev, [camId]: stream }));
    const el = videoRefs.current[camId];
    if (el) {
      el.srcObject = stream;
      el.play().catch(() => {});
    }
  }, []);

  const requestStream = useCallback((camId, socket) => {
    socket.emit('request-stream', { camId });
  }, []);

  const createPeer = useCallback((camId, fromSocketId, socket) => {
    // Close existing peer for this camera if any
    if (peersRef.current[camId]) {
      peersRef.current[camId].close();
    }

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    pc.onicecandidate = ({ candidate }) => {
      if (candidate) socket.emit('ice-candidate', { to: fromSocketId, candidate });
    };

    pc.ontrack = (event) => {
      const stream = event.streams[0];
      if (stream) attachStream(camId, stream);
    };

    pc.onconnectionstatechange = () => {
      console.log(`[PC] ${camId} state: ${pc.connectionState}`);
    };

    peersRef.current[camId] = pc;
    return pc;
  }, [attachStream, requestStream]);

  useEffect(() => {
    if (Notification.permission === 'default') Notification.requestPermission();

    const socket = io(SERVER_URL, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      setSocketConnected(true);
      socket.emit('register-viewer');
    });

    socket.on('disconnect', () => setSocketConnected(false));

    socket.on('camera-list', (list) => {
      setCameras(list);
      list.forEach(cam => requestStream(cam.camId, socket));
    });

    socket.on('camera-online', ({ camId, camName }) => {
      setCameras(prev => {
        if (prev.find(c => c.camId === camId)) return prev;
        return [...prev, { camId, camName, online: true }];
      });
      requestStream(camId, socket);
    });

    socket.on('camera-offline', ({ camId }) => {
      setCameras(prev => prev.filter(c => c.camId !== camId));
      setStreams(prev => { const s = { ...prev }; delete s[camId]; return s; });
      if (peersRef.current[camId]) {
        peersRef.current[camId].close();
        delete peersRef.current[camId];
      }
    });

    socket.on('motion-alert', ({ camId, camName }) => addAlert(camId, camName));

    socket.on('new-clip', (clip) => setClips(prev => [clip, ...prev].slice(0, 50)));

    // Camera sends offer to viewer
    socket.on('offer', async ({ from, offer, camId }) => {
      if (!camId) return;
      const pc = createPeer(camId, from, socket);

      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      socket.emit('answer', { to: from, answer });
    });

    socket.on('answer', async ({ from, answer }) => {
      // Find peer by socket id
      const entry = Object.entries(peersRef.current).find(([, pc]) => pc._fromSocket === from);
      if (entry) await entry[1].setRemoteDescription(new RTCSessionDescription(answer));
    });

    socket.on('ice-candidate', async ({ from, candidate, camId }) => {
      if (!candidate) return;
      // Apply only to the correct peer using camId
      if (camId && peersRef.current[camId]) {
        try { await peersRef.current[camId].addIceCandidate(new RTCIceCandidate(candidate)); } catch {}
      }
    });

    fetchClips();

    return () => {
      socket.disconnect();
      Object.values(peersRef.current).forEach(pc => pc.close());
    };
  }, [addAlert, createPeer, requestStream]);

  // When streams change, attach to video elements
  useEffect(() => {
    Object.entries(streams).forEach(([camId, stream]) => {
      const el = videoRefs.current[camId];
      if (el && el.srcObject !== stream) {
        el.srcObject = stream;
        el.play().catch(() => {});
      }
    });
  }, [streams]);

  async function fetchClips() {
    try {
      const res = await fetch(`${SERVER_URL}/api/clips`);
      setClips(await res.json());
    } catch {}
  }

  async function deleteClip(fileName) {
    try {
      await fetch(`${SERVER_URL}/api/clips/${fileName}`, { method: 'DELETE' });
      setClips(prev => prev.filter(c => c.fileName !== fileName));
    } catch {}
  }

  return (
    <div className="viewer-page">
      <div className="viewer-bg" />
      <header className="viewer-header">
        <button className="back-btn" onClick={onBack}>← ATRÁS</button>
        <div className="viewer-title-group">
          <span className="viewer-title">PANEL DE CONTROL</span>
          <span className="viewer-subtitle">SENTINEL SECURITY</span>
        </div>
        <div className="header-right">
          {alerts.length > 0 && (
            <div className="alert-indicator" onClick={() => setTab('alerts')}>
              <span className="dot red" />
              <span>{alerts.length} ALERTAS</span>
            </div>
          )}
          <div className="conn-badge">
            <span className={`dot ${socketConnected ? 'green' : 'gray'}`} />
            <span>{socketConnected ? 'CONECTADO' : 'DESCONECTADO'}</span>
          </div>
        </div>
      </header>

      <div className="viewer-tabs">
        <button className={`tab ${tab === 'live' ? 'active' : ''}`} onClick={() => setTab('live')}>
          📹 EN VIVO {cameras.length > 0 && <span className="tab-badge">{cameras.length}</span>}
        </button>
        <button className={`tab ${tab === 'clips' ? 'active' : ''}`} onClick={() => { setTab('clips'); fetchClips(); }}>
          🎞️ GRABACIONES {clips.length > 0 && <span className="tab-badge">{clips.length}</span>}
        </button>
        <button className={`tab ${tab === 'alerts' ? 'active' : ''}`} onClick={() => setTab('alerts')}>
          🚨 ALERTAS {alerts.length > 0 && <span className="tab-badge alert-badge">{alerts.length}</span>}
        </button>
      </div>

      <div className="viewer-content">
        {tab === 'live' && (
          <div className="live-tab">
            {cameras.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📡</div>
                <h3>Sin cámaras conectadas</h3>
                <p>Abre la app en el dispositivo cámara y selecciona "Modo Cámara"</p>
                <div className="empty-url">URL: <span>{window.location.origin}?mode=camera</span></div>
              </div>
            ) : (
              <div className={`cameras-grid cams-${Math.min(cameras.length, 4)}`}>
                {cameras.map(cam => (
                  <CameraFeed
                    key={cam.camId}
                    cam={cam}
                    stream={streams[cam.camId]}
                    onFullscreen={() => setActiveCamera(cam)}
                    videoRefs={videoRefs}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'clips' && (
          <div className="clips-tab">
            {clips.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🎞️</div>
                <h3>Sin grabaciones</h3>
                <p>Las grabaciones aparecen cuando se detecta movimiento</p>
              </div>
            ) : (
              <div className="clips-grid">
                {clips.map((clip, i) => (
                  <div key={i} className="clip-card animate-in">
                    <div className="clip-header">
                      <span className="clip-cam">{clip.camName || clip.camId}</span>
                      <button className="clip-delete" onClick={() => deleteClip(clip.fileName)}>✕</button>
                    </div>
                    <video src={`${SERVER_URL}${clip.url}`} controls className="clip-video" />
                    <div className="clip-meta">
                      <span>{new Date(clip.timestamp).toLocaleString('es-ES')}</span>
                      <span>{(clip.size / 1024 / 1024).toFixed(1)} MB</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'alerts' && (
          <div className="alerts-tab">
            {alerts.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">✅</div>
                <h3>Sin alertas</h3>
                <p>Todo tranquilo.</p>
              </div>
            ) : (
              <div className="alerts-list">
                <div className="alerts-header">
                  <span>{alerts.length} alertas</span>
                  <button className="clear-btn" onClick={() => setAlerts([])}>LIMPIAR TODO</button>
                </div>
                {alerts.map(alert => (
                  <div key={alert.id} className="alert-item animate-in">
                    <div className="alert-left">
                      <span className="dot red" />
                      <div>
                        <div className="alert-cam">{alert.camName || alert.camId}</div>
                        <div className="alert-time">{alert.date} — {alert.time}</div>
                      </div>
                    </div>
                    <div className="alert-tag">MOVIMIENTO</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {activeCamera && (
        <div className="fullscreen-overlay" onClick={() => setActiveCamera(null)}>
          <div className="fullscreen-content" onClick={e => e.stopPropagation()}>
            <button className="fs-close" onClick={() => setActiveCamera(null)}>✕</button>
            <div className="fs-label">{activeCamera.camName || activeCamera.camId}</div>
            <CameraFeed cam={activeCamera} stream={streams[activeCamera.camId]} fullscreen videoRefs={videoRefs} />
          </div>
        </div>
      )}
    </div>
  );
}

function CameraFeed({ cam, stream, onFullscreen, fullscreen, videoRefs }) {
  const videoEl = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    if (videoEl.current) {
      videoRefs.current[cam.camId] = videoEl.current;
    }
  }, [cam.camId, videoRefs]);

  useEffect(() => {
    if (videoEl.current && stream) {
      videoEl.current.srcObject = stream;
      videoEl.current.onplaying = () => setPlaying(true);
      videoEl.current.play().catch(() => {});
    }
  }, [stream]);

  const scale = (rotation === 90 || rotation === 270) ? 0.56 : 1;

  return (
    <div className={`camera-feed ${fullscreen ? 'fs-feed' : ''}`}>
      <div className="feed-top-bar">
        <div className="feed-info">
          <span className="dot green" />
          <span className="feed-name">{cam.camName || cam.camId}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ display: 'flex', gap: '3px' }}>
            {[0, 90, 180, 270].map(r => (
              <button key={r} onClick={() => setRotation(r)} style={{
                padding: '2px 5px', fontSize: '10px',
                background: rotation === r ? '#00ff88' : '#1a2030',
                color: rotation === r ? '#050608' : '#7a8899',
                border: '1px solid #2a3550', borderRadius: '2px', cursor: 'pointer'
              }}>{r}°</button>
            ))}
          </div>
          {!fullscreen && <button className="feed-expand" onClick={onFullscreen}>⤢</button>}
        </div>
      </div>
      <div className="feed-video-wrap">
        <video
          ref={videoEl}
          autoPlay playsInline
          className="feed-video"
          style={{ transform: `rotate(${rotation}deg) scale(${scale})`, transition: 'transform 0.3s ease' }}
        />
        {!playing && (
          <div className="feed-connecting">
            <div className="connecting-spinner" />
            <span>Conectando...</span>
          </div>
        )}
        <div className="feed-grid-overlay" />
      </div>
      <div className="feed-footer">
        <span className="feed-id">{cam.camId}</span>
        <span className="feed-live">● EN VIVO</span>
      </div>
    </div>
  );
}
